// Deterministic "at a glance" facts used by the location facts table on city,
// county and region pages. Everything is derived from the location dataset so
// the numbers stay in step with the phase currently live.

import type { LocationTarget } from "@/content/locations.data";

/** Dominant employment sector by region, used where no per-town data exists. */
const REGION_SECTOR: Record<string, string> = {
  London: "Professional, financial & creative services",
  "South East": "Professional services, tech & logistics",
  "South West": "Tourism, hospitality & advanced manufacturing",
  "East of England": "Agri-food, logistics & life sciences",
  "East Midlands": "Manufacturing, logistics & distribution",
  "West Midlands": "Manufacturing, automotive & professional services",
  "North West": "Digital, health & advanced manufacturing",
  "North East": "Manufacturing, energy & public sector services",
  "Yorkshire and the Humber": "Manufacturing, retail & financial services",
  Wales: "Public sector, manufacturing & tourism",
  Scotland: "Energy, finance & tourism",
};

/** Central-London and big-city cores skew heavily to office-based sectors. */
function sectorFor(sample: LocationTarget): string {
  const base = REGION_SECTOR[sample.region] ?? "Professional services & manufacturing";
  if (sample.region === "London" && sample.businesses >= 30000) {
    return "Financial, legal & professional services";
  }
  if (sample.tier === "City" && sample.businesses >= 25000) {
    return `${base.split(",")[0]}, professional services & retail`;
  }
  return base;
}

/** Fixed-fee retainer guide band, scaled by local employer density. */
function retainerFor(businesses: number, multiSite: boolean): string {
  if (multiSite) return businesses >= 40000 ? "£250–£600 per month" : "£200–£500 per month";
  if (businesses >= 25000) return "£250–£500 per month";
  if (businesses >= 8000) return "£200–£350 per month";
  return "From £200 per month";
}

export interface LocationFact {
  label: string;
  value: string;
}

export function cityFacts(location: LocationTarget, townsInCounty: number): LocationFact[] {
  return [
    { label: "Location type", value: `${location.placeType} · ${location.county}` },
    { label: "Local authority", value: location.lga },
    {
      label: `Businesses registered across ${location.lga}`,
      value: `${location.businesses.toLocaleString("en-GB")} (council-area total, shared by every town in ${location.lga})`,
    },
    {
      label: "Towns covered nearby",
      value: `${townsInCounty} in ${location.county}`,
    },
    { label: "Dominant sectors", value: sectorFor(location) },
    { label: "Typical retainer", value: retainerFor(location.businesses, false) },
  ];
}

/** Business counts are reported per local authority, so several towns share a
 *  figure — dedupe by LGA before totalling. */
function totalBusinesses(cities: LocationTarget[]): number {
  const seen = new Map<string, number>();
  for (const c of cities) if (!seen.has(c.lga)) seen.set(c.lga, c.businesses);
  return [...seen.values()].reduce((sum, n) => sum + n, 0);
}

/** Largest centre: highest employer density, preferring a city over a suburb. */
function largestCentre(cities: LocationTarget[]): LocationTarget | undefined {
  const rank = (t: LocationTarget) => (t.tier === "City" ? 0 : t.tier === "Town" ? 1 : 2);
  return [...cities].sort((a, b) => b.businesses - a.businesses || rank(a) - rank(b))[0];
}

export function countyFacts(county: string, cities: LocationTarget[]): LocationFact[] {
  const businesses = totalBusinesses(cities);
  const sample = largestCentre(cities);
  return [
    { label: "Towns & cities covered", value: `${cities.length} in ${county}` },
    {
      label: "Businesses registered across the councils we cover here",
      value: `${businesses.toLocaleString("en-GB")} (sum of council-area totals, not a ${county} boundary figure)`,
    },
    { label: "Largest centre", value: sample ? sample.city : county },
    { label: "Dominant sectors", value: sample ? sectorFor(sample) : "Mixed employer base" },
    { label: "Typical retainer", value: retainerFor(businesses, true) },
    { label: "Coverage", value: "On-site visits plus 24/7 remote advice" },
  ];
}

export function regionFacts(
  region: string,
  counties: number,
  cities: LocationTarget[],
): LocationFact[] {
  const businesses = totalBusinesses(cities);
  const sample = largestCentre(cities);
  return [
    { label: "Counties covered", value: `${counties} in ${region}` },
    { label: "Towns & cities covered", value: String(cities.length) },
    {
      label: "Businesses registered across the councils we cover here",
      value: `${businesses.toLocaleString("en-GB")} (sum of council-area totals, not a ${region} boundary figure)`,
    },
    { label: "Largest centre", value: sample ? sample.city : region },
    { label: "Dominant sectors", value: sample ? sectorFor(sample) : "Mixed employer base" },
    { label: "Typical retainer", value: retainerFor(businesses, true) },
  ];
}
